import React, { useContext, useState, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  CardActions,
  Button,
  Typography,
  Grid,
  Modal,
  CircularProgress,
  Container,
  Chip,
  IconButton,
  Divider,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  Description as DescriptionIcon,
  Assessment as AssessmentIcon,
  EngineeringOutlined as EngineeringIcon,
  ListAlt as ListIcon,
  BarChart as ChartIcon,
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon,
  LocationCity as PropertyIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import PropertyContext from "../contexts/PropertyContext";
import GenerateDRList from "./GenerateDRList";
import GenerateDRListPDF from "./GenerateDRListPDF";
import GenerateDRLFromTemplate from "./GenerateDRLFromTemplate";
import GenerateRoomsChart from "./GenerateRoomsChart";
import generatePropertyIssuesPDF from "./generatePropertyIssuesPDF";
import PMReportButton from "./reports/PMReportButton";
import GenerateOpenRoomIssuesPDF from "./reports/GenerateOpenRoomIssuesPDF";
import PMReportButtonFillTemplatePrefetch from "./reports/PMReportButton_FillTemplate";
import PMValidationReport from "./reports/PMValidationReport";
import GeneratePropertyIssuesExcel from "./reports/GeneratePropertyIssuesExcel";
import GenerateSDSReport from "./reports/GenerateSDSReport";
import GenerateRoomDowntimeAging from "./reports/GenerateRoomDowntimeAging";
import GenerateCostToMakeReady from "./reports/GenerateCostToMakeReady";
import GenerateStatusActivityLog from "./reports/GenerateStatusActivityLog";
import GenerateDailyOpsSummary from "./reports/GenerateDailyOpsSummary";
import GenerateOpenWorkOrdersAging from "./reports/GenerateOpenWorkOrdersAging";
import GenerateCompletedWorkOrdersByEmployee from "./reports/GenerateCompletedWorkOrdersByEmployee";
import GenerateMODCompliance from "./reports/GenerateMODCompliance";
import GenerateOpenPurchaseOrders from "./reports/GenerateOpenPurchaseOrders";
import GeneratePOSpendByVendor from "./reports/GeneratePOSpendByVendor";
import GenerateCompletedDispatchByEmployee from "./reports/GenerateCompletedDispatchByEmployee";
import { firestore } from "../firebase";

const ImprovedReportsPage = ({ user }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const { selectedProperty, selectedClient, rooms } = useContext(PropertyContext);

  const handleGeneratePropertyIssuesPDF = async () => {
    setStatusMessage("Generating Property Issues Report...");
    setIsGenerating(true);
    await generatePropertyIssuesPDF(selectedClient, selectedProperty, setStatusMessage, user);
    setIsGenerating(false);
  };

  const handleGenerateDRListPDF = async () => {
    setStatusMessage("Generating DRL PDF...");
    setIsGenerating(true);
    await GenerateDRListPDF(selectedClient, selectedProperty, rooms, setStatusMessage);
    setIsGenerating(false);
  };

  // Report categories with cards
  const reportCategories = useMemo(
    () => [
      {
        title: "Operations",
        description: "Daily snapshot and compliance",
        icon: <AssessmentIcon sx={{ fontSize: 40, color: "#00695c" }} />,
        color: "#e0f2f1",
        reports: [
          {
            name: "Daily Operations Summary",
            description: "Status counts, rooms up today, open WOs/issues, MOD %",
            component: <GenerateDailyOpsSummary user={user} />,
            type: "component",
          },
          {
            name: "MOD Completion Compliance",
            description: "Daily % of MOD tasks completed (date range)",
            component: <GenerateMODCompliance user={user} />,
            type: "component",
          },
        ],
      },
      {
        title: "Work Orders",
        description: "Open work order aging and completion productivity",
        icon: <EngineeringIcon sx={{ fontSize: 40, color: "#5d4037" }} />,
        color: "#efebe9",
        reports: [
          {
            name: "Open Work Orders (Aging)",
            description: "All open work orders by age, room, assignee, PTE",
            component: <GenerateOpenWorkOrdersAging user={user} />,
            type: "component",
          },
          {
            name: "Completed WOs by Employee",
            description: "Work orders completed per employee (date range)",
            component: <GenerateCompletedWorkOrdersByEmployee user={user} />,
            type: "component",
          },
        ],
      },
      {
        title: "Dispatch",
        description: "Dispatch assignment and completion",
        icon: <EngineeringIcon sx={{ fontSize: 40, color: "#1565c0" }} />,
        color: "#e3f2fd",
        reports: [
          {
            name: "Completed Dispatch by Employee",
            description: "Assigned vs. completed dispatch tasks (date range)",
            component: <GenerateCompletedDispatchByEmployee user={user} />,
            type: "component",
          },
        ],
      },
      {
        title: "Purchase Orders",
        description: "Outstanding POs and vendor spend",
        icon: <AssessmentIcon sx={{ fontSize: 40, color: "#ad1457" }} />,
        color: "#fce4ec",
        reports: [
          {
            name: "Open Purchase Orders",
            description: "Outstanding POs with expected totals and age",
            component: <GenerateOpenPurchaseOrders user={user} />,
            type: "component",
          },
          {
            name: "PO Spend by Vendor",
            description: "PO spend grouped by vendor (date range)",
            component: <GeneratePOSpendByVendor user={user} />,
            type: "component",
          },
        ],
      },
      {
        title: "Rooms & Activity",
        description: "Downtime, cost-to-make-ready, and status activity",
        icon: <AssessmentIcon sx={{ fontSize: 40, color: "#6a1b9a" }} />,
        color: "#f3e5f5",
        reports: [
          {
            name: "Room Downtime Aging",
            description: "Down rooms by days down with estimated cost to bring up",
            component: <GenerateRoomDowntimeAging user={user} />,
            type: "component",
          },
          {
            name: "Cost to Make Ready",
            description: "Total cost to bring all down rooms up, grouped by vendor",
            component: <GenerateCostToMakeReady user={user} />,
            type: "component",
          },
          {
            name: "Status Activity Log",
            description: "Who changed which room status, when (date range)",
            component: <GenerateStatusActivityLog user={user} />,
            type: "component",
          },
        ],
      },
      {
        title: "Down Room Lists",
        description: "Generate comprehensive down room reports",
        icon: <ListIcon sx={{ fontSize: 40, color: "#1976d2" }} />,
        color: "#e3f2fd",
        reports: [
          {
            name: "DRL Excel",
            description: "Export down room list to Excel format",
            component: <GenerateDRList />,
            type: "component",
          },
          {
            name: "DRL from Template",
            description: "Generate Suzie's Down Rooms List",
            component: <GenerateDRLFromTemplate />,
            type: "component",
          },
          {
            name: "DRL PDF with Images",
            description: "Generate PDF report with room images",
            action: handleGenerateDRListPDF,
            type: "action",
          },
        ],
      },
      {
        title: "Property Management",
        description: "PM reports and validation tools",
        icon: <AssessmentIcon sx={{ fontSize: 40, color: "#2e7d32" }} />,
        color: "#e8f5e9",
        reports: [
          {
            name: "PM Report",
            description: "Standard property management report",
            component: (
              <PMReportButton
                rooms={rooms}
                currentUserName={user.fullName || user?.email || "Unknown"}
              />
            ),
            type: "component",
          },
          {
            name: "PM Report (Template)",
            description: "PM report using template prefetch",
            component: (
              <PMReportButtonFillTemplatePrefetch
                db={firestore}
                clientId={selectedClient}
                selectedProperty={selectedProperty}
                rooms={rooms}
                currentUserName={user.fullName || user?.email || "Unknown"}
              />
            ),
            type: "component",
          },
          {
            name: "PM Validation Report",
            description: "Validate property management data",
            component: (
              <PMValidationReport
                clientId={selectedClient}
                selectedProperty={selectedProperty}
                rooms={rooms}
                currentUserName={user.fullName || user?.email || "Unknown"}
              />
            ),
            type: "component",
          },
          {
            name: "Property Issues Report",
            description: "Detailed property issues and maintenance needs",
            action: handleGeneratePropertyIssuesPDF,
            type: "action",
          },
          {
            name: "Property Issues (Excel)",
            description: "Export property issues to Excel format",
            component: <GeneratePropertyIssuesExcel />,
            type: "component",
          },
          {
            name: "SDS Report",
            description: "Generate Safety Data Sheets report with TOC",
            component: <GenerateSDSReport />,
            type: "component",
          },
        ],
      },
      {
        title: "Analytics & Charts",
        description: "Visual data and analytics",
        icon: <ChartIcon sx={{ fontSize: 40, color: "#ed6c02" }} />,
        color: "#fff3e0",
        reports: [
          {
            name: "Rooms Chart",
            description: "Visual chart of room status distribution",
            component: <GenerateRoomsChart user={user} />,
            type: "component",
          },
        ],
      },
    ],
    [rooms, selectedProperty, selectedClient, user]
  );

  // Placeholder reports (to be implemented)
  const upcomingReports = [
    {
      name: "Work Orders by User",
      description: "Track completed work orders by employee",
      icon: <CheckIcon />,
      status: "Coming Soon",
    },
    {
      name: "Manager Checklist",
      description: "Daily manager task checklist",
      icon: <ListIcon />,
      status: "Coming Soon",
    },
    {
      name: "Vendor Needed Report",
      description: "Rooms requiring vendor services",
      icon: <EngineeringIcon />,
      status: "Coming Soon",
    },
    {
      name: "Easiest Rooms Report",
      description: "Identify quickest turnaround opportunities",
      icon: <ScheduleIcon />,
      status: "Coming Soon",
    },
  ];

  // Filter reports by the search query (matches report name/description, or the
  // whole category when its title/description matches).
  const normalizedQuery = searchQuery.trim().toLowerCase();

  const filteredCategories = useMemo(() => {
    if (!normalizedQuery) return reportCategories;
    return reportCategories
      .map((category) => {
        const categoryMatch = `${category.title} ${category.description}`
          .toLowerCase()
          .includes(normalizedQuery);
        const reports = categoryMatch
          ? category.reports
          : category.reports.filter((r) =>
              `${r.name} ${r.description}`
                .toLowerCase()
                .includes(normalizedQuery)
            );
        return { ...category, reports };
      })
      .filter((category) => category.reports.length > 0);
  }, [reportCategories, normalizedQuery]);

  const filteredUpcoming = !normalizedQuery
    ? upcomingReports
    : upcomingReports.filter((r) =>
        `${r.name} ${r.description}`.toLowerCase().includes(normalizedQuery)
      );

  const hasResults =
    filteredCategories.length > 0 || filteredUpcoming.length > 0;

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box mb={4}>
        <Typography variant="h4" fontWeight="600" gutterBottom>
          Reports & Analytics
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Generate comprehensive reports for{" "}
          {selectedProperty?.displayName || "your property"}
        </Typography>
      </Box>

      {/* Search filter */}
      <Box mb={4} sx={{ maxWidth: 480, mx: "auto" }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search reports..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="clear search"
                  onClick={() => setSearchQuery("")}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
      </Box>

      {/* No results */}
      {!hasResults && (
        <Box py={6} textAlign="center">
          <Typography variant="h6" color="text.secondary">
            No reports match "{searchQuery}"
          </Typography>
        </Box>
      )}

      {/* Report Categories */}
      {filteredCategories.map((category, idx) => (
        <Box key={idx} mb={5}>
          {/* Category Header */}
          <Box
            display="flex"
            alignItems="center"
            gap={2}
            mb={3}
            sx={{
              p: 2,
              backgroundColor: category.color,
              borderRadius: 2,
              borderLeft: "4px solid",
              borderLeftColor: category.icon.props.sx.color,
            }}
          >
            {category.icon}
            <Box>
              <Typography variant="h6" fontWeight="600">
                {category.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {category.description}
              </Typography>
            </Box>
          </Box>

          {/* Reports Grid */}
          <Grid container spacing={3}>
            {category.reports.map((report, reportIdx) => (
              <Grid item xs={12} sm={6} md={4} key={reportIdx}>
                <Card
                  elevation={2}
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: 6,
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" fontWeight="600" gutterBottom>
                      {report.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {report.description}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ p: 2, pt: 0 }}>
                    {report.type === "component" ? (
                      <Box width="100%">{report.component}</Box>
                    ) : (
                      <Button
                        variant="contained"
                        fullWidth
                        startIcon={<DownloadIcon />}
                        onClick={report.action}
                        disabled={isGenerating}
                      >
                        Generate
                      </Button>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}

      {/* Upcoming Reports Section */}
      {filteredUpcoming.length > 0 && (
      <Box mt={6}>
        <Divider sx={{ mb: 3 }} />
        <Typography variant="h5" fontWeight="600" gutterBottom>
          Upcoming Reports
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          These reports are currently in development
        </Typography>

        <Grid container spacing={2}>
          {filteredUpcoming.map((report, idx) => (
            <Grid item xs={12} sm={6} md={3} key={idx}>
              <Card
                sx={{
                  opacity: 0.7,
                  position: "relative",
                }}
              >
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    {report.icon}
                    <Chip label={report.status} size="small" color="warning" />
                  </Box>
                  <Typography variant="subtitle1" fontWeight="600">
                    {report.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {report.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
      )}

      {/* Loading Modal */}
      <Modal
        open={isGenerating}
        onClose={() => {}}
        aria-labelledby="generating-report"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
            textAlign: "center",
          }}
        >
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Generating Report
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {statusMessage || "Please wait..."}
          </Typography>
        </Box>
      </Modal>
    </Container>
  );
};

export default ImprovedReportsPage;
